import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiRequest } from '../auth/apiClient';
import { usePagination, SeeMoreButton } from './usePagination';
import { ArchiveButton, ArchivedBadge, RestoreButton, ShowArchivedToggle } from './ArchiveControls';
import './PropertiesList.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  active: 'Active',
  ending_soon: 'Ending soon',
  vacant: 'Vacant',
  eviction_in_progress: 'Eviction',
};

const COMP_LABEL = {
  green: 'All valid',
  amber: 'Expiring',
  red: 'Action required',
  none: 'No docs',
};

/**
 * TASK 1 — Properties top-level navigation list view.
 *
 * Each row shows the property full address, current tenant or "Vacant",
 * landlord or "No landlord linked", a tenancy status badge, a
 * compliance health indicator, the active S8 stage (if any), and the
 * count of open maintenance requests. Clicking a row navigates to
 * `/properties/:id` (the redesigned tabbed Property Detail page —
 * Task 2).
 */
export default function PropertiesList({ initialView = 'all' }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // 2026-05-29 — Let / To Let / All view toggle.
  // The initial value can be passed in from the sidebar dropdown.
  const [view, setView] = useState(initialView); // 'all' | 'to_let' | 'let'

  // 2026-05-29 — When the user toggles the sidebar Properties dropdown
  // (Available vs Let), App.js re-renders this component with a NEW
  // `initialView` prop but React re-uses the same instance and so the
  // internal `view` state stays stale. Mirror the prop into the state
  // whenever the prop changes so the table immediately switches.
  useEffect(() => {
    setView(initialView);
  }, [initialView]);
  // 2026-05-29 — Track in-flight delete so we can disable the row.
  const [deletingId, setDeletingId] = useState(null);
  // 2026-05-06 (Item 4 Part C) — inline Link-Landlord state. Mirrors
  // the dropdown pattern already used by PropertiesWithoutLandlordBanner
  // so clicking the amber "No landlord linked" badge opens an inline
  // selector with the agency's existing landlord profiles. The agent
  // can link without leaving the Properties list.
  const [landlords, setLandlords] = useState([]);
  const [linkOpenFor, setLinkOpenFor] = useState(null);  // property_id
  const [linking, setLinking] = useState(null);          // property_id
  // 2026-02 FIX 2 — Show archived toggle (off by default).
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { ok, data } = await apiRequest('GET', `${BACKEND_URL}/api/landlords`);
        if (!cancelled && ok && Array.isArray(data)) setLandlords(data);
      } catch { /* keep empty */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { ok, data } = await apiRequest(
          'GET', `${BACKEND_URL}/api/properties/list-with-summary`);
        if (!cancelled && ok) setItems(Array.isArray(data) ? data : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = items;
    // 2026-02 FIX 2 — Hide archived rows unless toggle on.
    base = base.filter(p => showArchived ? true : !p.archived);
    // 2026-05-29 — Apply Let / To Let split.
    if (view === 'to_let') {
      base = base.filter(p =>
        (p.availability_status === 'available'
         || p.availability_status === 'under_offer')
        && p.tenancy_status !== 'active'
        && p.tenancy_status !== 'ending_soon'
        && p.tenancy_status !== 'eviction_in_progress');
    } else if (view === 'let') {
      base = base.filter(p =>
        p.tenancy_status === 'active'
        || p.tenancy_status === 'ending_soon'
        || p.tenancy_status === 'eviction_in_progress');
    }
    if (!q) return base;
    return base.filter(p =>
      (p.address || '').toLowerCase().includes(q) ||
      (p.tenant_name || '').toLowerCase().includes(q) ||
      (p.landlord_name || '').toLowerCase().includes(q));
  }, [items, search, view, showArchived]);

  // 2026-02 FIX 1 — 25-per-page pagination.
  const propsPager = usePagination(filtered, 25);
  const visibleFiltered = propsPager.visible;

  const open = (id) => navigate(`/properties/${id}`);

  // 2026-05-29 — Hard-delete a property record (refused server-side if
  // there's an active tenancy, open S8 case, or open maintenance).
  const removeProperty = async (propertyId, address) => {
    if (!window.confirm(
        `Remove this property?\n\n${address}\n\n` +
        `This permanently deletes the property record. Cannot be undone. ` +
        `It will be refused if the property has an active tenancy, an open ` +
        `Section 8 case, or any open maintenance request.`)) {
      return;
    }
    setDeletingId(propertyId);
    try {
      const { ok, data } = await apiRequest(
        'DELETE', `${BACKEND_URL}/api/properties/${propertyId}`);
      if (ok) {
        setItems(prev => prev.filter(p => p.property_id !== propertyId));
      } else {
        window.alert('Could not remove property: ' +
          ((data && data.detail) || 'unknown error'));
      }
    } finally {
      setDeletingId(null);
    }
  };

  // 2026-05-06 (Item 4 Part C) — link a landlord to a property from the
  // inline selector. Mirrors the PUT call used by the dashboard
  // PropertiesWithoutLandlordBanner so behaviour stays consistent.
  const linkLandlord = async (propertyId, landlordId) => {
    setLinking(propertyId);
    try {
      const { ok, data } = await apiRequest(
        'PUT',
        `${BACKEND_URL}/api/properties/${propertyId}/landlord`,
        { landlord_id: landlordId },
      );
      if (ok) {
        setItems(prev => prev.map(p => p.property_id === propertyId
          ? { ...p, landlord_id: landlordId,
              landlord_name: (landlords.find(l => (l._id || l.id) === landlordId)?.full_name)
                          || (landlords.find(l => (l._id || l.id) === landlordId)?.company_name)
                          || p.landlord_name }
          : p));
        setLinkOpenFor(null);
      } else {
        window.alert('Could not link landlord: ' + ((data && data.detail) || 'unknown error'));
      }
    } finally {
      setLinking(null);
    }
  };

  return (
    <div className="props-list-page" data-testid="properties-list-page">
      <div className="props-list-header">
        <div>
          <h1 className="props-list-title">Properties</h1>
          <div className="props-list-meta" data-testid="props-list-count">
            {loading ? 'Loading…' : `${filtered.length} of ${items.length} properties`}
          </div>
        </div>
        <div className="props-search-box" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <ShowArchivedToggle value={showArchived} onChange={setShowArchived} testid="props-show-archived" />
          <input
            type="search"
            placeholder="Search address, tenant or landlord…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="props-list-search"
          />
        </div>
      </div>

      {/* 2026-05-29 — Let / To Let split toggle. */}
      <div data-testid="props-view-tabs" style={{
        display: 'flex', gap: '0.5rem', marginBottom: '1rem',
        flexWrap: 'wrap',
      }}>
        {[
          ['all', `All (${items.length})`],
          ['to_let', `To Let (${items.filter(p =>
            (p.availability_status === 'available'
             || p.availability_status === 'under_offer')
            && p.tenancy_status !== 'active'
            && p.tenancy_status !== 'ending_soon'
            && p.tenancy_status !== 'eviction_in_progress').length})`],
          ['let', `Let (${items.filter(p =>
            p.tenancy_status === 'active'
            || p.tenancy_status === 'ending_soon'
            || p.tenancy_status === 'eviction_in_progress').length})`],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            data-testid={`props-tab-${key}`}
            style={{
              padding: '0.5rem 1.1rem',
              border: '1px solid ' + (view === key ? '#0073EA' : '#cbd5e1'),
              background: view === key ? '#0073EA' : '#ffffff',
              color: view === key ? '#ffffff' : '#0f172a',
              borderRadius: 6,
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="props-table-wrap">
        <table className="props-table">
          <thead>
            <tr>
              <th>Address</th>
              <th>Current Tenant</th>
              <th>Landlord</th>
              <th>Tenancy</th>
              <th>Compliance</th>
              <th>S8 Stage</th>
              <th>Open Maint.</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="props-empty">
                {items.length === 0
                  ? 'No properties yet. Add your first property from the dashboard.'
                  : 'No properties match your search.'}
              </td></tr>
            )}
            {visibleFiltered.map(p => (
              <tr
                key={p.property_id}
                onClick={() => open(p.property_id)}
                data-testid={`props-row-${p.property_id}`}
                style={p.archived ? { opacity: 0.6 } : undefined}
              >
                <td>
                  <Link
                    to={`/properties/${p.property_id}`}
                    className="props-address"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`props-address-${p.property_id}`}
                  >
                    {p.address || '(no address)'}
                  </Link>
                </td>
                <td>
                  {p.tenant_id ? (
                    <Link
                      to={`/tenants/${p.tenant_id}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#0f172a', textDecoration: 'none' }}
                    >
                      {p.tenant_name}
                    </Link>
                  ) : (
                    <span className="props-tenant-vacant">{p.tenant_name}</span>
                  )}
                </td>
                <td>
                  {p.landlord_id ? (
                    <Link
                      to={`/landlords/${p.landlord_id}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#0f172a', textDecoration: 'none' }}
                    >
                      {p.landlord_name}
                    </Link>
                  ) : (
                    /* 2026-05-06 (Item 4 Part C) — amber "No landlord
                       linked" badge. Click toggles the inline selector
                       below; the existing PUT /properties/:id/landlord
                       endpoint links the chosen landlord. */
                    <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => setLinkOpenFor(
                          linkOpenFor === p.property_id ? null : p.property_id)}
                        data-testid={`props-no-landlord-badge-${p.property_id}`}
                        style={{
                          background: '#fffbeb', color: '#92400e',
                          border: '1px solid #fcd34d', borderRadius: 999,
                          padding: '0.2rem 0.65rem', fontSize: '0.8rem',
                          fontWeight: 600, cursor: 'pointer',
                        }}>
                        No landlord linked
                      </button>
                      {linkOpenFor === p.property_id && (
                        <div
                          data-testid={`props-link-landlord-dropdown-${p.property_id}`}
                          style={{
                            position: 'absolute', top: '110%', left: 0, zIndex: 20,
                            background: '#fff', border: '1px solid #e2e8f0',
                            borderRadius: 8, boxShadow: '0 6px 20px rgba(15,23,42,0.12)',
                            padding: '0.5rem', minWidth: 240,
                          }}>
                          {landlords.length === 0 ? (
                            <div style={{ padding: '0.5rem', fontSize: '0.85rem', color: '#64748b' }}>
                              No landlord profiles yet.{' '}
                              <a href="/?view=settings&tab=landlords"
                                 style={{ color: '#0073EA', fontWeight: 600 }}>
                                Add one in Settings
                              </a>.
                            </div>
                          ) : (
                            landlords.map(ll => (
                              <button
                                key={ll._id || ll.id}
                                type="button"
                                disabled={linking === p.property_id}
                                onClick={() => linkLandlord(p.property_id, ll._id || ll.id)}
                                data-testid={`props-link-landlord-select-${p.property_id}-${ll._id || ll.id}`}
                                style={{
                                  display: 'block', width: '100%', textAlign: 'left',
                                  background: 'transparent', border: 'none',
                                  padding: '0.5rem 0.6rem', borderRadius: 4,
                                  cursor: linking === p.property_id ? 'wait' : 'pointer',
                                  fontSize: '0.88rem', color: '#0f172a',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                                {ll.full_name}{ll.company_name ? ` (${ll.company_name})` : ''}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </td>
                <td>
                  <span className={`props-badge props-badge-${p.tenancy_status}`}
                        data-testid={`props-status-${p.property_id}`}>
                    {STATUS_LABEL[p.tenancy_status] || p.tenancy_status}
                  </span>
                </td>
                <td>
                  <span className={`props-comp-dot props-comp-${p.compliance_health}`}
                        data-testid={`props-comp-${p.property_id}`}>
                    {COMP_LABEL[p.compliance_health] || ''}
                  </span>
                </td>
                <td>
                  {p.active_case_stage ? (
                    <span className="props-s8-chip"
                          data-testid={`props-s8-${p.property_id}`}>
                      Stage {p.active_case_stage}
                    </span>
                  ) : (
                    <span className="props-s8-empty">—</span>
                  )}
                </td>
                <td>
                  <span className={`props-maint-chip ${p.open_maintenance_count > 0 ? 'has-open' : ''}`}
                        data-testid={`props-maint-${p.property_id}`}>
                    {p.open_maintenance_count}
                  </span>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  {p.archived ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ArchivedBadge small />
                      <RestoreButton kind="property" id={p.property_id}
                                      onRestored={() => setItems(prev => prev.map(x => x.property_id === p.property_id ? { ...x, archived: false } : x))} />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <ArchiveButton
                        kind="property"
                        id={p.property_id}
                        size="xs"
                        testid={`props-archive-${p.property_id}`}
                        onArchived={() => setItems(prev => prev.map(x => x.property_id === p.property_id ? { ...x, archived: true } : x))}
                      />
                      {/* 2026-05-29 — Remove Property. Confirmed before
                          sending DELETE; backend refuses if a tenancy /
                          active S8 / open maintenance exists. */}
                      <button
                        type="button"
                        data-testid={`props-remove-${p.property_id}`}
                        disabled={deletingId === p.property_id}
                        onClick={() => removeProperty(p.property_id, p.address)}
                        style={{
                          padding: '0.25rem 0.55rem',
                          background: deletingId === p.property_id ? '#94a3b8' : '#fff',
                          color: deletingId === p.property_id ? '#fff' : '#b91c1c',
                          border: '1px solid #fecaca',
                          borderRadius: 6,
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          cursor: deletingId === p.property_id ? 'wait' : 'pointer',
                        }}
                      >
                        {deletingId === p.property_id ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <SeeMoreButton hasMore={propsPager.hasMore} remaining={propsPager.remaining} onClick={propsPager.showMore} testid="properties-list" />
      </div>
    </div>
  );
}
